import { t } from '@lingui/core/macro'

/**
 * Converts a string to title case.
 * Replaces hyphens and underscores with spaces, then capitalizes each word.
 * Handles all caps, mixed case, and lowercase text.
 *
 * @param input - The string to convert to title case
 * @returns The title-cased string
 *
 * @example
 * toTitleCase("HELLO_WORLD") // "Hello World"
 * toTitleCase("hello-world") // "Hello World"
 * toTitleCase("hELLo WoRLd") // "Hello World"
 */
export function toTitleCase(input: string): string {
  // Replace hyphens and underscores with spaces
  const cleanedInput = input.replace(/[-_]/g, ' ')

  // Split the string into words, capitalize each word, and join them back together
  const titleCased = cleanedInput
    .split(' ')
    .map((word) => {
      // Handle empty strings
      if (!word) return word
      // Convert entire word to lowercase first, then capitalize first letter
      // This ensures all caps (e.g., "HELLO") becomes "Hello"
      const lowercased = word.toLowerCase()
      return lowercased.charAt(0).toUpperCase() + lowercased.slice(1)
    })
    .join(' ')

  return titleCased
}

/**
 * Resolves the display title for a document.
 *
 * When a real document title exists (i.e. it's distinct from the filename
 * fallback), it is returned verbatim so we preserve the author's intended
 * casing and punctuation. Otherwise the filename fallback is prettified via
 * {@link toTitleCase} since it's typically a slug-like value.
 *
 * @param documentTitle - The document's real title, if any
 * @param fallbackTitle - The filename-derived fallback title
 */
export function formatDocumentDisplayTitle(
  documentTitle: string | null | undefined,
  fallbackTitle: string
): string {
  if (documentTitle && documentTitle !== fallbackTitle) {
    return documentTitle
  }
  return toTitleCase(documentTitle || fallbackTitle)
}

export function formatDrawingMetadata(
  drawingNumber: string | null | undefined,
  sheetNumber: string | null | undefined
): string | null {
  const normalizedDrawingNumber = drawingNumber?.trim()
  if (!normalizedDrawingNumber) return null
  const normalizedSheetNumber = sheetNumber?.trim()
  if (!normalizedSheetNumber) return normalizedDrawingNumber
  return t`${normalizedDrawingNumber} - s.${normalizedSheetNumber}`
}

/**
 * Finds contiguous runs of adjacent `<mark>` tags in an HTML fragment and
 * returns them as plain-text phrases, longest first.
 *
 * For example, given:
 *   `<mark>Fuel</mark> <mark>gas</mark> combustion <mark>supply</mark> <mark>pressure</mark>`
 * Returns: `["supply pressure", "Fuel gas"]`
 *
 * These are real phrases that exist contiguously in the source document,
 * unlike the full search query which may match scattered words.
 */
export function extractContiguousMarkedPhrases(html: string): string[] {
  const regex = /<mark>[^<]*<\/mark>(\s*<mark>[^<]*<\/mark>)*/g
  const matches = html.match(regex)
  if (!matches) return []

  const seen = new Set<string>()
  return matches
    .map((m) => m.replace(/<\/?mark>/g, '').trim())
    .filter((phrase) => {
      if (!phrase || seen.has(phrase)) return false
      seen.add(phrase)
      return true
    })
    .sort((a, b) => b.length - a.length)
}

const QUOTE_EDGE_WORDS = 6
const MIN_QUOTE_EDGE_CHARS = 6
const MAX_QUOTE_TAGS = 4

const isTagLike = (word: string): boolean =>
  word.length >= MIN_QUOTE_EDGE_CHARS && /[a-z]/i.test(word) && /\d/.test(word)

const lineWords = (line: string): string[] => line.split(/\s+/).filter(Boolean)

function quoteAttempts(quote: string): string[][] {
  const lines = quote
    .split(/\n+/)
    .map(lineWords)
    .filter((words) => words.length > 0)
  if (lines.length === 0) return []

  const whole = lines.flat().join(' ')
  const edges = [
    lines[0].slice(0, QUOTE_EDGE_WORDS).join(' '),
    lines[lines.length - 1].slice(-QUOTE_EDGE_WORDS).join(' '),
  ].filter(
    (edge) =>
      edge !== whole && edge.replace(/\s/g, '').length >= MIN_QUOTE_EDGE_CHARS
  )
  const tags = [...new Set(lines.flat().filter(isTagLike))]
    .filter((tag) => tag !== whole)
    .slice(0, MAX_QUOTE_TAGS)
  return uniqueAttempts([[whole], [...new Set(edges)], tags])
}

function uniqueAttempts(attempts: string[][]): string[][] {
  const seen = new Set<string>()
  return attempts.filter((attempt) => {
    const key = attempt.join('\n')
    if (attempt.length === 0 || seen.has(key)) return false
    seen.add(key)
    return true
  })
}

export function quoteSearchAttempts(quote: string): string[][] {
  const normalized = quote.normalize('NFKC')
  return uniqueAttempts([
    ...quoteAttempts(normalized),
    ...(normalized === quote ? [] : quoteAttempts(quote)),
  ])
}
